import {
  consumeSearchParam,
  resolveApiBaseUrl,
  usableClerkPublishableKey,
  usablePublicMapToken,
} from '@croissants/ui';

/**
 * The only place this app reads configuration.
 *
 * No domain, host or absolute URL is hardcoded anywhere in the portal (PRD
 * OPS-018): the API origin arrives as `VITE_API_BASE_URL` and the identity
 * application as `VITE_CLERK_INTERNAL_PUBLISHABLE_KEY`, so moving from
 * mycroissant.paris to croissantinbed.com is a configuration change and not a
 * code change.
 *
 * That variable keeps its name and changes its VALUE: it now holds the GOOGLE
 * application the pilot gate already used, because `internal.` and the gate
 * authenticate the same people for the same reason and became one application
 * (plan website-app-split section 4). Being able to sign in is not authorization
 * - a Google application admits any Google account - so the API checks every
 * signer against PILOT_ALLOWED_EMAILS on every `/internal` request.
 */

function read(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * WHICH INSTANCE this bundle was built for.
 *
 * One domain and one code path serve two worlds - the demo instance on the `d`
 * hosts and production on the clean ones - and until now nothing in the portal
 * could tell them apart. It did not have to: every difference lived in the
 * worker's configuration. The Pilot tab is the first thing that must be absent
 * from one of them rather than merely refused by the API, so the build needs the
 * one fact it never had.
 *
 * `VITE_ENVIRONMENT` carries it, mirroring the worker's own `ENVIRONMENT` var
 * name for name, and it is read here with every other piece of configuration
 * rather than at the screen that acts on it. It is NOT a host check: no host is
 * hardcoded anywhere in this portal (PRD OPS-018), and one that were would have
 * to be edited again for the move to croissantinbed.com.
 *
 * Anything other than the exact string is not production - an unset variable, a
 * local run, a preview build. The safe default is the instance that HAS the tab,
 * because the cost of showing it on the demo instance is nothing and the cost of
 * a screen that cannot be reached is an operator who cannot open the portals.
 * The API is the authority either way: it refuses the cookie in production
 * whatever this build believes.
 */
export function isProductionInstance(value: string): boolean {
  return value.trim().toLowerCase() === 'production';
}

export type AuthMode = 'clerk' | 'dev' | 'unconfigured';

/**
 * Which sign-in mechanism this build OFFERS.
 *
 * Clerk whenever a real publishable key is configured. Otherwise the portal
 * falls back to the API's `X-Dev-Role` header bypass - which the worker
 * hard-refuses in production - so that fallback is only ever offered on a
 * development build. A deployed build with no key is `unconfigured` and says so,
 * rather than silently degrading to an authentication mode the API will reject.
 *
 * There is no `pilot` mode any more, and its absence is deliberate. It was a
 * build flag (`VITE_PILOT_MODE=1`) that replaced the sign-in entirely with "wait
 * for a cookie somebody else minted", which cannot be right for the portal that
 * now MINTS that cookie. A valid `cib_pilot` cookie still opens this portal -
 * the API accepts it on `/internal` however this build signs in, which is what keeps
 * the headless-agent bypass working - but it is a fallback the gate tries when
 * no Clerk session exists (`auth.tsx`), not a mode that hides the sign-in.
 *
 * `devAuthForced` exists for the rehearsal and end-to-end suites: a development
 * build may hold a real Clerk key and still need to drive the portal without a
 * human at a sign-in prompt. It is ignored outright unless `isDevBuild` is true.
 */
export function chooseAuthMode(input: {
  clerkKey: string | null;
  isDevBuild: boolean;
  devAuthForced: boolean;
}): AuthMode {
  if (input.isDevBuild && input.devAuthForced) return 'dev';
  if (input.clerkKey !== null) return 'clerk';
  return input.isDevBuild ? 'dev' : 'unconfigured';
}

const DEV_AUTH_FLAG = 'cib.internal.devAuthForced';

/**
 * What a link asked of the bypass: `?devAuth=1` turns it on, `?devAuth=0` turns
 * it off again, and anything else - another `devAuth` value included - says
 * nothing (null).
 *
 * `?dev=<role>` - the signed-in entry `scripts/dev-preview.mjs` opens this portal
 * on - implies `devAuth=1`: a link that names a development role has asked for
 * the bypass, and without this it landed on the Google sign-in whenever a Clerk
 * key was configured locally, with the role never read.
 */
export function requestedDevAuth(search: string): boolean | null {
  const params = new URLSearchParams(search);
  const requested = params.get('devAuth') ?? (params.has('dev') ? '1' : null);
  if (requested === '1') return true;
  if (requested === '0') return false;
  return null;
}

/**
 * Whether this browser has the bypass on: a link's request, remembered, else
 * what was remembered before. Never consulted on a production build.
 *
 * The request is consumed once (`consumeSearchParam`, `@croissants/ui`): only
 * `devAuth` is stripped, and `?dev=` stays for `auth.tsx`, which reads it later
 * - this runs at module evaluation, before any render.
 */
function readDevAuthForced(isDevBuild: boolean): boolean {
  if (!isDevBuild) return false;
  try {
    const asked = consumeSearchParam('devAuth', requestedDevAuth);
    if (asked === true) {
      globalThis.localStorage?.setItem(DEV_AUTH_FLAG, '1');
      return true;
    }
    if (asked === false) {
      globalThis.localStorage?.removeItem(DEV_AUTH_FLAG);
      return false;
    }
    return globalThis.localStorage?.getItem(DEV_AUTH_FLAG) === '1';
  } catch {
    return false;
  }
}

export interface InternalAdminConfig {
  /** Prefix every request is built from. Empty means same-origin. */
  readonly apiBaseUrl: string;
  /** False when `VITE_API_BASE_URL` was never set - the portal then says so. */
  readonly apiConfigured: boolean;
  readonly clerkPublishableKey: string | null;
  readonly authMode: AuthMode;
  readonly isDevBuild: boolean;
  /**
   * True only on a build made for the production instance (`VITE_ENVIRONMENT`).
   * The Pilot tab, its route and the cookie probe that goes with it are left out
   * of that build entirely - see `isProductionInstance` above.
   */
  readonly isProductionInstance: boolean;
  /**
   * Null when `VITE_MAPBOX_PUBLIC_TOKEN` is unset or is not a publishable
   * token (`usablePublicMapToken`, `@croissants/ui`). The coverage map then says which variable is missing instead of
   * rendering an empty canvas (OPS-004).
   */
  readonly mapboxPublicToken: string | null;
}

export function readConfig(): InternalAdminConfig {
  const configuredApiBaseUrl = read('VITE_API_BASE_URL');
  const clerkKey = usableClerkPublishableKey(read('VITE_CLERK_INTERNAL_PUBLISHABLE_KEY'));
  const isDevBuild = import.meta.env.DEV === true;
  const devAuthForced = readDevAuthForced(isDevBuild);

  return {
    // Where requests go: the one resolver every portal shares (@croissants/ui,
    // F6). The dev server proxies `/internal`, so a cross-origin value becomes
    // the same-origin empty prefix there and stays as configured in a build.
    apiBaseUrl: resolveApiBaseUrl({
      configured: configuredApiBaseUrl,
      dev: isDevBuild,
      origin: globalThis.location?.origin,
    }),
    apiConfigured: configuredApiBaseUrl !== '',
    clerkPublishableKey: clerkKey,
    authMode: chooseAuthMode({ clerkKey, isDevBuild, devAuthForced }),
    isDevBuild,
    isProductionInstance: isProductionInstance(read('VITE_ENVIRONMENT')),
    mapboxPublicToken: usablePublicMapToken(read('VITE_MAPBOX_PUBLIC_TOKEN')),
  };
}

export const config: InternalAdminConfig = readConfig();
